// Actividad: what happened in the workshop, newest first, for every role.
// Board moves (collection group over `movimientos`), inspections and customer
// notices merged and grouped by day, like the prototype's "Actividad reciente".
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import {
  useActividad,
  useAvisos,
  useConfig,
  useInspecciones,
  useUsuarios,
} from '@/datos/consultas';
import { desdeRango } from '@/inspecciones/filtros';
import { fechaLarga, inicioDelDia, useAhora } from '@/lib/tiempo';
import { Encabezado } from '@/shell/Encabezado';
import { ErrorCarga, Esqueleto, Segmentado, Tarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { Contenedor } from '@/ui/pagina';
import { TextoPatente } from '@/ui/patente';
import { eventosEstadia, LineaDeTiempo, type Evento } from '@/vehiculos/Historial';

const tipos = ['todo', 'tablero', 'inspecciones', 'avisos'] as const;
const PASO = 150;
const DIA = 86_400_000;

export const Route = createFileRoute('/_app/actividad')({
  validateSearch: z.object({ tipo: z.enum(tipos).optional().catch(undefined) }),
  component: Pagina,
});

function Pagina() {
  const { tipo = 'todo' } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const ahora = useAhora();
  const hoy = inicioDelDia(ahora);
  const [n, setN] = useState(PASO);
  const config = useConfig();
  const movs = useActividad(n);
  const insp = useInspecciones(desdeRango('7', hoy));
  const avisos = useAvisos(null);
  const usuarios = useUsuarios();

  const nombres = useMemo(
    () => new Map((usuarios.data ?? []).map((u) => [u.id, u.nombre])),
    [usuarios.data],
  );

  const dias = useMemo(() => {
    const fases = config.data?.fases ?? [];
    // Movement ids are only unique within their order: qualify them.
    const m = (movs.data ?? []).map((x) => ({ ...x, id: `${x.ordenId}-${x.id}` }));
    const i = (insp.data ?? []).filter((x) => x.estado !== 'borrador');
    const a = avisos.data ?? [];
    const patente = new Map<string, string>();
    for (const x of m) if (x.patente) patente.set(`m-${x.id}`, x.patente);
    for (const x of i) patente.set(`i-${x.id}`, x.patente);
    for (const x of a) patente.set(`a-${x.id}`, x.patente);
    const eventos = eventosEstadia(
      tipo === 'todo' || tipo === 'tablero' ? m : [],
      tipo === 'todo' || tipo === 'inspecciones' ? i : [],
      tipo === 'todo' || tipo === 'avisos' ? a : [],
      fases,
      (u) => (u ? nombres.get(u) : undefined),
    ).map((e) => {
      const p = patente.get(e.clave);
      return p
        ? ({
            ...e,
            prefijo: (
              <Link
                to="/vehiculos/$patente"
                params={{ patente: p }}
                className="hover:text-primary-text"
              >
                <TextoPatente patente={p} className="text-[16px]" />
              </Link>
            ),
          } satisfies Evento)
        : e;
    });
    const grupos = new Map<number, Evento[]>();
    for (const e of eventos) {
      const d = inicioDelDia(e.en || ahora);
      grupos.set(d, [...(grupos.get(d) ?? []), e]);
    }
    return [...grupos.entries()].sort((x, y) => y[0] - x[0]);
  }, [movs.data, insp.data, avisos.data, config.data, nombres, tipo, ahora]);

  const cargando = movs.isPending || insp.isPending || avisos.isPending;
  const error = movs.error ?? insp.error ?? avisos.error;
  const etiquetaDia = (d: number) =>
    d === hoy ? 'Hoy' : d === inicioDelDia(hoy - DIA / 2) ? 'Ayer' : fechaLarga(new Date(d));

  return (
    <>
      <Encabezado
        titulo="Actividad"
        ayuda="actividad"
        subtitulo="Lo último que pasó en el taller"
      />
      <Contenedor ancho="angosto">
        <Segmentado
          etiqueta="Mostrar"
          valor={tipo}
          onCambio={(v) =>
            void navegar({ search: { tipo: v === 'todo' ? undefined : v }, replace: true })
          }
          opciones={[
            { valor: 'todo', texto: 'Todo' },
            { valor: 'tablero', texto: 'Tablero' },
            { valor: 'inspecciones', texto: 'Inspecciones' },
            { valor: 'avisos', texto: 'Avisos' },
          ]}
          className="max-w-full self-start overflow-x-auto"
        />
        {error ? (
          <ErrorCarga error={error} />
        ) : cargando ? (
          <Tarjeta className="flex flex-col gap-4 p-5" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <Esqueleto key={i} className="h-12" />
            ))}
          </Tarjeta>
        ) : dias.length === 0 ? (
          <Tarjeta>
            <Vacio icono={<Activity />} titulo="Sin actividad registrada todavía">
              Aquí aparecen los movimientos del tablero, las inspecciones y los avisos al cliente.
            </Vacio>
          </Tarjeta>
        ) : (
          <>
            {dias.map(([d, eventos]) => (
              <section key={d} aria-labelledby={`dia-${d}`}>
                <h2
                  id={`dia-${d}`}
                  className="mb-2.5 text-[13px] font-bold tracking-[0.06em] text-text-2 uppercase first-letter:uppercase"
                >
                  {etiquetaDia(d)}
                  <span className="tabular ml-2 font-normal normal-case">· {eventos.length}</span>
                </h2>
                <Tarjeta className="p-4 sm:p-5">
                  <LineaDeTiempo eventos={eventos} />
                </Tarjeta>
              </section>
            ))}
            {(movs.data?.length ?? 0) >= n && (
              <Boton className="self-center" onClick={() => setN((x) => x + PASO)}>
                Ver movimientos anteriores
              </Boton>
            )}
            <p className="text-center text-[13px] text-text-2">
              Inspecciones y avisos de los últimos 7 días.
            </p>
          </>
        )}
      </Contenedor>
    </>
  );
}
